import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "space-y-2" }
const _hoisted_4 = { class: "ml-2 flex items-center space-x-4" }
const _hoisted_5 = {
  key: 0,
  class: "mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
}
const _hoisted_6 = { class: "w-full border-collapse text-sm" }
const _hoisted_7 = { class: "sticky top-0 bg-gray-200 text-gray-800" }
const _hoisted_8 = { class: "align-center p-4" }
const _hoisted_9 = { class: "flex flex-1 items-center" }
const _hoisted_10 = { class: "flex flex-col" }
const _hoisted_11 = { class: "font-semibold text-gray-800" }
const _hoisted_12 = { class: "text-sm text-gray-400" }
const _hoisted_13 = { class: "flex flex-col items-center justify-center space-y-1 px-1 py-2" }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "align-center p-4" }
const _hoisted_16 = { class: "flex justify-center gap-2" }

import { ref, onMounted, onUnmounted, computed, watch, ComputedRef } from 'vue';
import orderBy from 'lodash/orderBy';

import { UserIcon } from '@heroicons/vue/solid';
import { PauseIcon, PlayIcon } from '@heroicons/vue/outline';

import { useFilterStore } from '@/stores';
import { RealtimeReportService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import {
    RealtimeReportServiceGetUserDevicesResult,
    UserDeviceType,
    UserDeviceStatus,
    UserModel,
    UserDeviceStateModel,
    AgentExtensionReportGetters,
    AgentExtensionReportActions,
    AgentExtensionReportFilter,
    SortMenuItem,
    SortMenuItemDirection,
    Permission,
    SelectOptionsServiceGetUserLabelsResult,
    FilterItem,
} from '@/types';
import {
    AgentExtensionReportFilters,
    AgentExtensionReportSort,
} from '@/views/pages/reports/agent-extension/components';
import store from '@/store';
import { useI18n } from 'vue-i18n';

type UserDeviceTable = Partial<
    Record<
        UserDeviceType,
        {
            state: UserDeviceStateModel | null;
            status: UserDeviceStatus;
        }
    >
>;

interface UserTableRow {
    user: RealtimeReportUser;
    devices: UserDeviceTable;
}

interface RealtimeReportUser extends UserModel {
    labels?: SelectOptionsServiceGetUserLabelsResult[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentExtensionReportPage',
  setup(__props) {

const { t } = useI18n();

// global filter state
const filterStore = useFilterStore();
const levelFilter = computed(() => filterStore.level);
const filter = computed<AgentExtensionReportFilter>(
    () => store.getters[AgentExtensionReportGetters.FILTER],
);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[AgentExtensionReportGetters.ACTIVE_FILTERS],
);

const search = computed<string>({
    get: () => store.getters[AgentExtensionReportGetters.SEARCH],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[AgentExtensionReportGetters.SORT],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SORT, value),
});

// Reactive state
const userDevices = ref<RealtimeReportServiceGetUserDevicesResult[]>([]);
const isLoading = ref(false);
const isRunning = ref(false);

const tableHeaders = [
    {
        key: 'name',
        label: 'report.realtimeAgentExtension.details.userName',
        align: 'left',
    },
    {
        key: 'mobile',
        label: 'report.realtimeAgentExtension.details.mobile',
        align: 'center',
    },
    {
        key: 'desktop',
        label: 'report.realtimeAgentExtension.details.desktop',
        align: 'center',
    },
    {
        key: 'web',
        label: 'report.realtimeAgentExtension.details.web',
        align: 'center',
    },
    {
        key: 'external',
        label: 'report.realtimeAgentExtension.details.external',
        align: 'center',
    },
    {
        key: 'queue',
        label: 'report.realtimeAgentExtension.details.queues',
        align: 'center',
    },
];

const deviceTypes = [
    UserDeviceType.MOBILE,
    UserDeviceType.DESKTOP,
    UserDeviceType.WEB,
    UserDeviceType.EXTERNAL,
];

let pollingTimeoutId: number | null = null;

const connectedUserDeviceStatuses = new Set([
    UserDeviceStatus.CONNECTED,
    UserDeviceStatus.DIALING,
    UserDeviceStatus.RING,
    UserDeviceStatus.RINGING,
    UserDeviceStatus.INUSE,
    UserDeviceStatus.IN_CALL,
]);

const computedUserTableRows = computed<UserTableRow[]>(() => {
    const users = new Map<number, UserTableRow>();

    for (const userDevice of userDevices.value) {
        const extensionNumber = userDevice.user.extension!.number;
        let user = users.get(extensionNumber);

        if (!user) {
            user = {
                user: userDevice.user,
                devices: {},
            };

            users.set(extensionNumber, user);
        }

        const status = userDevice.user.queueStatusHistory?.length
            ? UserDeviceStatus.IN_PAUSE
            : (userDevice.state?.status as UserDeviceStatus | undefined) ||
              UserDeviceStatus.OFFLINE;

        user.devices[userDevice.deviceType as UserDeviceType] = {
            state: userDevice.state,
            status,
        };
    }

    let rows = Array.from(users.values());

    if (filter.value.status) {
        rows = rows.filter((row) =>
            Object.values(row.devices).some(
                (device) => device?.status === filter.value.status?.value,
            ),
        );
    }

    if (filter.value.user) {
        rows = rows.filter((row) => row.user.extension?.number === filter.value.user?.id);
    }

    if (filter.value.userLabel) {
        rows = rows.filter((row) =>
            row.user.labels?.some((label) => label.id === filter.value.userLabel?.id),
        );
    }

    if (filter.value.queue) {
        rows = rows.filter((row) =>
            row.user.queues?.some((queue) => queue.id === filter.value.queue?.id),
        );
    }

    if (search.value) {
        const searchValue = search.value.toLowerCase().trim();

        rows = rows.filter(
            (row) =>
                row.user.name.toLowerCase().includes(searchValue) ||
                row.user.extension?.number?.toString().includes(searchValue),
        );
    }

    const sortFields: Record<string, (row: UserTableRow) => string | number> = {
        name: (row) => row.user.name.toLowerCase(),
        extension: (row) => row.user.extension!.number,
    };

    const selectedSorts = sortMenuSelected.value;

    if (selectedSorts.length) {
        return orderBy(
            rows,
            selectedSorts.map((sort) => sortFields[sort.key]),
            selectedSorts.map((sort) =>
                sort.direction === SortMenuItemDirection.ASC ? 'asc' : 'desc',
            ),
        );
    }

    return orderBy(rows, [sortFields.name, sortFields.extension], ['asc', 'asc']);
});

// Called periodically to update real-time data
const fetchData = async () => {
    try {
        isLoading.value = true;
        const usersResp = await RealtimeReportService.getUserDevices();
        userDevices.value = usersResp.data;
    } catch (err) {
        toastServiceError(err);
    } finally {
        isLoading.value = false;
    }
};

// Starts polling every 5s — restarts automatically if still running
const startPolling = async () => {
    isRunning.value = true;
    const loop = async () => {
        if (!isRunning.value) return;
        await fetchData();
        if (isRunning.value) pollingTimeoutId = window.setTimeout(loop, 5000);
    };
    loop();
};

// Stops polling on unmount or when filters change
const stopPolling = () => {
    isRunning.value = false;
    if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
};

// Lifecycle: start/stop polling
onMounted(() => startPolling());
onUnmounted(() => stopPolling());

const toggleQueueMemberPause = async (userId: number) => {
    try {
        const response = await RealtimeReportService.toggleQueueMemberPause(userId);
        toast.success(response.data.message);
        await fetchData();
    } catch (error) {
        toastServiceError(error);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(AgentExtensionReportActions.CLEAR_FILTER, filter.field);
};

// Restart polling on level change — ensures data stays scoped to current filter
watch([levelFilter], () => {
    stopPolling();
    startPolling();
});

return (_ctx: any,_cache: any) => {
  const _component_UiSpinner = _resolveComponent("UiSpinner")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiActionButton = _resolveComponent("UiActionButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createTextVNode(_toDisplayString(_ctx.$t('report.realtimeAgentExtension.title')) + " ", 1),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_UiSpinner, {
                key: 0,
                class: "theme-color ml-2 h-6 w-6"
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_UiPanel, {
        class: "flex px-4 py-2",
        "no-padding": ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_UiListSearch, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event))
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(AgentExtensionReportSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(AgentExtensionReportFilters))
          ])
        ]),
        _: 1
      })
    ]),
    (activeFilters.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
            return (_openBlock(), _createBlock(_component_UiBadge, {
              key: filter,
              removable: "",
              onRemove: ($event: any) => (handleRemoveFilter(filter))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(`report.realtimeAgentExtension.filters.activeTag.${filter.field}`)) + " " + _toDisplayString(filter.label), 1)
              ]),
              _: 2
            }, 1032, ["onRemove"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_UiPanel, { "no-padding": "" }, {
      default: _withCtx(() => [
        _createElementVNode("table", _hoisted_6, [
          _createElementVNode("thead", _hoisted_7, [
            _createElementVNode("tr", null, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tableHeaders, (header) => {
                return _createElementVNode("th", {
                  key: header.key,
                  class: _normalizeClass(["p-4", header.align === 'center' ? 'text-center' : 'text-left'])
                }, _toDisplayString(_ctx.$t(header.label)), 3)
              }), 64))
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedUserTableRows.value, (row) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: row.user.id,
                class: "even:bg-gray-100"
              }, [
                _createElementVNode("td", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createVNode(_unref(UserIcon), { class: "mr-2 h-12 w-12 rounded-full border border-gray-300 bg-gray-50 p-0.5 text-gray-400 shadow" }),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, _toDisplayString(row.user.name), 1),
                      _createElementVNode("div", _hoisted_12, " Ramal " + _toDisplayString(row.user.extension?.number), 1)
                    ])
                  ])
                ]),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(deviceTypes, (deviceType) => {
                  return _createElementVNode("td", {
                    key: deviceType,
                    class: "align-center p-4 text-center"
                  }, [
                    _createElementVNode("div", _hoisted_13, [
                      (row.devices[deviceType])
                        ? (_openBlock(), _createElementBlock("p", {
                            key: 0,
                            class: _normalizeClass([
                                        _unref(connectedUserDeviceStatuses).has(
                                            row.devices[deviceType]?.state
                                                ?.status as UserDeviceStatus,
                                        )
                                            ? 'bg-success-50 border-success-800 text-success-700'
                                            : 'bg-danger-50 border-danger-800 text-danger-700',
                                        'grid grid-cols-1 gap-1 rounded border-2 border-opacity-15 bg-gray-50 px-1 py-0.5 pt-1 text-center text-xs font-semibold',
                                    ])
                          }, _toDisplayString(_ctx.$t(
                                            `report.realtimeAgentExtension.device.status.${
                                                row.user?.queueStatusHistory?.length
                                                    ? _unref(UserDeviceStatus).IN_PAUSE
                                                    : row.devices[deviceType]?.status ||
                                                      _unref(UserDeviceStatus).OFFLINE
                                            }`,
                                        )), 3))
                        : (_openBlock(), _createElementBlock("p", _hoisted_14, "-"))
                    ])
                  ])
                }), 64)),
                _createElementVNode("td", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.user.queues, (queue) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: queue.id,
                          class: "flex gap-1"
                        }, [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(
                                                _ctx.$can(_unref(Permission).VIEW_QUEUE) ? 'RouterLink' : 'span'
                                            ), {
                            to: {
                                                name: 'ViewQueueById',
                                                params: {
                                                    id: queue?.id,
                                                },
                                            },
                            class: "text-primary"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(queue.name), 1)
                            ]),
                            _: 2
                          }, 1032, ["to"]))
                        ]))
                      }), 128))
                    ]),
                    (row.user.queues?.length && _ctx.$can(_unref(Permission).EDIT_QUEUE))
                      ? (_openBlock(), _createBlock(_component_UiActionButton, {
                          key: 0,
                          variant: "primary",
                          "text-variant": "primary",
                          onClick: ($event: any) => (toggleQueueMemberPause(row.user.id))
                        }, {
                          default: _withCtx(() => [
                            (!row.user?.queueStatusHistory?.length)
                              ? (_openBlock(), _createBlock(_unref(PauseIcon), {
                                  key: 0,
                                  class: "h-7 w-7"
                                }))
                              : (_openBlock(), _createBlock(_unref(PlayIcon), {
                                  key: 1,
                                  class: "h-7 w-7"
                                })),
                            _createElementVNode("span", null, _toDisplayString(row.user?.queueStatusHistory?.length
                                            ? _unref(t)('report.realtimeAgentExtension.actions.unpause')
                                            : _unref(t)('report.realtimeAgentExtension.actions.pause')), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]))
            }), 128))
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})